import { CountryFlag, ItemContent, cn } from "@aircall/ds";
import type { CountryCode } from "libphonenumber-js";
import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  type KeyboardEvent,
  createContext,
  forwardRef,
  useContext,
  useMemo,
} from "react";

import { formatInternational } from "@/helpers/phone_number/phone_number.helpers";

type LineContextValue = {
  countryCode: CountryCode | null;
  name: string | null;
  number: string | null;
  isClickable: boolean;
};

export interface LineProps extends ComponentPropsWithoutRef<"div"> {
  countryCode?: CountryCode | null;
  name?: string | null;
  number?: string | null;
}

export interface LineContentProps extends ComponentPropsWithoutRef<"div"> {}

export interface LineFlagProps extends ComponentPropsWithoutRef<"div"> {}

export interface LineTitleProps extends ComponentPropsWithoutRef<"span"> {
  /** Force the clickable affordance; defaults to the `Line` row's clickability. */
  clickable?: boolean;
}

export interface LineSubtitleProps extends ComponentPropsWithoutRef<"span"> {}

const LineContext = createContext<LineContextValue>({
  countryCode: null,
  name: null,
  number: null,
  isClickable: false,
});

function useLineContext() {
  return useContext(LineContext);
}

/**
 * Title styling. The name is always rendered in the default `text-foreground`
 * (a name is not an interactive accent colour). When the row is clickable the
 * affordance is an underline on row hover — never a green/`text-primary` tint.
 *
 * Expressed as a `cn`-based variants helper (mirroring the sibling `UserName`
 * block) rather than `cva`, which is not a first-class dependency in this app.
 */
export const lineTitleVariants = ({
  clickable = false,
}: { clickable?: boolean } = {}) =>
  cn(
    "truncate text-sm font-semibold text-foreground",
    clickable && "group-hover:underline"
  );

/**
 * Compound root for a "line" entry: a flag flex item vertically centred next to
 * a `LineContent` text stack (title + optional subtitle). The flag stays
 * centred whether there is a title only or a title and a subtitle.
 *
 * ```tsx
 * <Line countryCode="FR" name="Paris office" number="+33176420302" onClick={fn}>
 *   <LineFlag />
 *   <LineContent>
 *     <LineTitle />
 *     <LineSubtitle />
 *   </LineContent>
 * </Line>
 * ```
 */
export const Line = forwardRef<HTMLDivElement, Line.Props>(
  (componentProps: Line.Props, forwardRef: ForwardedRef<HTMLDivElement>) => {
    const {
      children,
      className,
      countryCode = null,
      name = null,
      number = null,
      onClick,
      ...props
    } = componentProps;

    const value = useMemo<LineContextValue>(
      () => ({ countryCode, name, number, isClickable: !!onClick }),
      [countryCode, name, number, onClick]
    );

    // A clickable row is rendered as a plain `div`, so it needs the button role
    // and keyboard activation (Enter / Space) to stay accessible. Re-dispatching
    // a real click keeps `onClick` typed as a mouse handler.
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.currentTarget.click();
      }
    };

    return (
      <LineContext.Provider value={value}>
        <div
          ref={forwardRef}
          data-slot="line"
          className={cn(
            "group flex items-center gap-x-1",
            onClick ? "cursor-pointer" : "cursor-default",
            className
          )}
          onClick={onClick}
          {...(onClick
            ? { role: "button", tabIndex: 0, onKeyDown: handleKeyDown }
            : {})}
          {...props}
        >
          {children ?? (
            <LineContent>
              <LineTitle />
            </LineContent>
          )}
        </div>
      </LineContext.Provider>
    );
  }
);
Line.displayName = "Line";

export namespace Line {
  export type Props = LineProps;
}

/**
 * Vertical text stack (title + optional subtitle) placed next to the flag.
 *
 * Composes the DS `ItemContent` subpart, overriding its `data-slot` back to
 * `line-content` and forcing the compact `flex min-w-0 flex-col` stack so the
 * public contract (slot name, props, layout) is unchanged.
 */
export const LineContent = forwardRef<HTMLDivElement, LineContent.Props>(
  (
    componentProps: LineContent.Props,
    forwardRef: ForwardedRef<HTMLDivElement>
  ) => {
    const { className, children, ...props } = componentProps;

    return (
      <ItemContent
        ref={forwardRef}
        data-slot="line-content"
        className={cn("flex min-w-0 flex-col", className)}
        {...props}
      >
        {children}
      </ItemContent>
    );
  }
);
LineContent.displayName = "LineContent";

export namespace LineContent {
  export type Props = LineContentProps;
}

/** Country flag column. Renders nothing when no country code is provided. */
export const LineFlag = forwardRef<HTMLDivElement, LineFlag.Props>(
  (
    componentProps: LineFlag.Props,
    forwardRef: ForwardedRef<HTMLDivElement>
  ) => {
    const { className, ...props } = componentProps;
    const { countryCode } = useLineContext();

    if (!countryCode) {
      return null;
    }

    return (
      <div
        ref={forwardRef}
        data-slot="line-flag"
        className={cn("shrink-0", className)}
        {...props}
      >
        <CountryFlag countryIsoCode={countryCode} size="md" />
      </div>
    );
  }
);
LineFlag.displayName = "LineFlag";

export namespace LineFlag {
  export type Props = LineFlagProps;
}

/**
 * Primary line. Renders its `children`, falling back to the row's `name`, then
 * to the formatted `number` from context — so a consumer can override the text
 * (`<LineTitle>{custom}</LineTitle>`) while the common case stays data-driven.
 */
export const LineTitle = forwardRef<HTMLSpanElement, LineTitle.Props>(
  (
    componentProps: LineTitle.Props,
    forwardRef: ForwardedRef<HTMLSpanElement>
  ) => {
    const { className, clickable, children, ...props } = componentProps;
    const { isClickable, name, number } = useLineContext();

    const content = children ?? name ?? formatInternational(number ?? "");
    const isClickableResolved = clickable ?? isClickable;

    return (
      <span
        ref={forwardRef}
        data-slot="line-title"
        className={cn(
          lineTitleVariants({ clickable: isClickableResolved }),
          className
        )}
        {...props}
      >
        {content}
      </span>
    );
  }
);
LineTitle.displayName = "LineTitle";

export namespace LineTitle {
  export type Props = LineTitleProps;
}

/**
 * Secondary, muted line. Renders its `children`, falling back to the formatted
 * `number` from context.
 */
export const LineSubtitle = forwardRef<HTMLSpanElement, LineSubtitle.Props>(
  (
    componentProps: LineSubtitle.Props,
    forwardRef: ForwardedRef<HTMLSpanElement>
  ) => {
    const { className, children, ...props } = componentProps;
    const { number } = useLineContext();

    const content = children ?? formatInternational(number ?? "");

    return (
      <span
        ref={forwardRef}
        data-slot="line-subtitle"
        className={cn(
          "truncate text-xs font-medium text-muted-foreground",
          className
        )}
        {...props}
      >
        {content}
      </span>
    );
  }
);
LineSubtitle.displayName = "LineSubtitle";

export namespace LineSubtitle {
  export type Props = LineSubtitleProps;
}
